import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { glyphStyle } from "@/lib/glyphStyle";

const STAFF_PREFIXES = ["/admin", "/pos", "/kitchen", "/ops", "/clock", "/myhours"];

const GLYPHS = {
  fall: ["🍁", "🍂", "🌰", "🎃", "🍎"],
  valentines: ["❤️", "💕", "🌹", "💘", "🍫"],
  halloween: ["🎃", "👻", "🦇", "🕸️", "🍬"],
  thanksgiving: ["🍂", "🍁", "🦃", "🌽", "🥧"],
  christmas: ["❄️", "🎄", "⛄", "🎁", "🍪"],
  easter: ["🐰", "🌷", "🐣", "🥚", "🦋"],
  july4: ["🎆", "⭐", "🎇", "🗽", "✨"],
};

const hexToTriplet = (hex) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

/** Applies the owner-selected seasonal theme to customer pages only:
 *  sets data-theme on <html> (palette swap via CSS vars) and rains
 *  cute floating glyphs. Staff screens (/admin /pos /kitchen /ops)
 *  always stay on the classic look. Also applies the owner's site-wide
 *  font scale + custom brand colors (custom colors yield to an active
 *  seasonal theme so holidays still look right). */
export default function SeasonalTheme() {
  const { settings } = useSiteSettings();
  const { pathname } = useLocation();
  const isStaff = STAFF_PREFIXES.some((p) => pathname.startsWith(p));
  const activeKey = !isStaff && settings.active_theme && settings.active_theme !== "default"
    ? settings.active_theme
    : null;
  const custom = activeKey?.startsWith("custom_")
    ? (settings.custom_themes || []).find((t) => t.key === activeKey) || null
    : null;
  // Unknown/deleted custom key → fall back to classic look.
  const theme = activeKey && (!activeKey.startsWith("custom_") || custom) ? activeKey : null;

  useEffect(() => {
    if (theme) document.documentElement.setAttribute("data-theme", custom ? "custom" : theme);
    else document.documentElement.removeAttribute("data-theme");
    return () => document.documentElement.removeAttribute("data-theme");
  }, [theme, custom]);

  // Site-wide font scale (customer pages only). Root font-size scales every
  // rem-based Tailwind size, like browser zoom.
  const fontScale = settings.font_scale ?? 100;
  useEffect(() => {
    const el = document.documentElement;
    if (!isStaff && fontScale !== 100) el.style.fontSize = `${fontScale}%`;
    else el.style.fontSize = "";
    return () => { el.style.fontSize = ""; };
  }, [isStaff, fontScale]);

  // Custom brand colors — only when no seasonal theme is active. Admin-made
  // custom themes supply their own palette via inline vars.
  const primary = hexToTriplet(custom ? custom.primary : settings.custom_primary_color);
  const accent = hexToTriplet(custom ? custom.accent : settings.custom_accent_color);
  useEffect(() => {
    const el = document.documentElement;
    const useCustom = !isStaff && (!theme || !!custom);
    if (useCustom && primary) el.style.setProperty("--brand-red", primary);
    else el.style.removeProperty("--brand-red");
    if (useCustom && accent) el.style.setProperty("--brand-yellow", accent);
    else el.style.removeProperty("--brand-yellow");
    return () => {
      el.style.removeProperty("--brand-red");
      el.style.removeProperty("--brand-yellow");
    };
  }, [isStaff, theme, custom, primary, accent]);

  // Stable pseudo-random layout per theme so re-renders don't reshuffle.
  const flakes = useMemo(() => {
    if (!theme) return [];
    const glyphs = custom ? (custom.glyphs || []) : (GLYPHS[theme] || []);
    if (!glyphs.length) return [];
    return Array.from({ length: 14 }, (_, i) => ({
      glyph: glyphs[i % glyphs.length],
      left: `${(i * 7.3 + ((i * 37) % 11)) % 100}%`,
      size: 14 + ((i * 53) % 16),
      duration: 10 + ((i * 41) % 9),
      delay: -((i * 29) % 14),
    }));
  }, [theme, custom]);

  if (!theme || !flakes.length) return null;
  return (
    <div
      aria-hidden="true"
      data-testid="seasonal-decorations"
      className="fixed inset-0 -z-10 pointer-events-none overflow-hidden"
    >
      {flakes.map((f, i) => (
        <span
          key={i}
          className="season-fall"
          style={{
            left: f.left,
            fontSize: `${f.size}px`,
            animationDuration: `${f.duration}s`,
            animationDelay: `${f.delay}s`,
            ...glyphStyle(custom?.glyph_color),
          }}
        >
          {f.glyph}
        </span>
      ))}
    </div>
  );
}
